import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  ListGroup,
  Row,
  Col,
  Card,
  Modal,
} from "react-bootstrap";
import { useFindUsers } from "../../api/queries/useFindUser";
import { useSaveNotification } from "../../api/mutations/useSaveNotification";
import { useIsMobile } from "../Hooks/useIsMobile";
import NotificationForm from "./NotificationForm";
import InfiniteScroll from "react-infinite-scroll-component";
import { User } from "../../types/Types";

const NotificationPanel: React.FC = () => {
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [fullUsersList, setFullUsersList] = useState<User[]>([]);

  const isMobile = useIsMobile();

  // Reset pagination and clear users list when search changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreUsers(true);
    setFullUsersList([]);
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const { data: userResponse } = useFindUsers({
    pageSize: 25,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
  });

  const { mutate: updateNotification, isPending } = useSaveNotification({
    onSuccess: () => {
      setSelectedUserIds([]);
      setFormData({ name: "", description: "" });
      setShowMessageModal(false);
      setShowConfirmationModal(true);
    },
  });

  useEffect(() => {
    if (userResponse) {
      const newUsers = userResponse?.users?.data ?? [];

      // If it's the first page, replace the list; otherwise append (avoid duplicates)
      if (currentPage === 1) {
        setFullUsersList(newUsers);
      } else {
        setFullUsersList((prevUsers) => {
          const existingIds = new Set(prevUsers.map((u) => u.id));
          const uniqueNewUsers = newUsers.filter((u) => !existingIds.has(u.id));
          return [...prevUsers, ...uniqueNewUsers];
        });
      }

      // Update hasMoreUsers based on totalPages
      const totalPages = userResponse?.users?.totalPages ?? 0;
      const currentPageNum = userResponse?.users?.pageNumber ?? currentPage;
      setHasMoreUsers(currentPageNum < totalPages);
    }
  }, [userResponse, currentPage]);

  const loadMoreUsers = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const toggleUser = (userId: number) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((item) => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    const payloads = selectedUserIds.length
      ? selectedUserIds.map((userId) => ({
          ...formData,
          userId,
          date: new Date().toISOString(),
          isRead: false,
          isDeleted: false,
        }))
      : [
          {
            ...formData,
            userId: 0,
            date: new Date().toISOString(),
            isRead: false,
            isDeleted: false,
          },
        ];

    payloads.forEach((payload) => updateNotification(payload));
  };

  // Use fullUsersList directly since filtering is done server-side
  const filteredUsers = useMemo(() => fullUsersList ?? [], [fullUsersList]);

  useEffect(() => {
    const allFilteredIds = filteredUsers.map((item) => item.id);
    const allSelected =
      allFilteredIds.length > 0 &&
      allFilteredIds.every((id) => selectedUserIds.includes(id));
    setSelectAll(allSelected);
  }, [filteredUsers, selectedUserIds]);

  return (
    <>
      <Row className="p-3">
        <Col xs={12} md={4}>
          <Card className="h-100">
            <Card.Header>
              <Row>
                <Col xs={12} className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Check
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    label="Select All"
                  />
                </Col>
              </Row>
            </Card.Header>

            <Card.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <ListGroup variant="flush">
                <InfiniteScroll
                  dataLength={filteredUsers.length}
                  next={loadMoreUsers}
                  hasMore={hasMoreUsers}
                  scrollThreshold={0.7}
                  loader={<div className="text-center">Loading...</div>}
                  endMessage={
                    <div className="text-center">No more users to load</div>
                  }
                >
                  {filteredUsers.map((item) => (
                    <ListGroup.Item key={item.id}>
                      <Form.Check
                        type="checkbox"
                        label={item.firstName + " " + item.lastName}
                        checked={selectedUserIds.includes(item.id)}
                        onChange={() => toggleUser(item.id)}
                      />
                    </ListGroup.Item>
                  ))}
                </InfiniteScroll>
              </ListGroup>
            </Card.Body>

            {isMobile && (
              <Card.Footer>
                <Button
                  className="w-100"
                  disabled={selectedUserIds.length < 1}
                  variant="primary"
                  onClick={() => setShowMessageModal(true)}
                >
                  Next
                </Button>
              </Card.Footer>
            )}
          </Card>
        </Col>

        {!isMobile && (
          <Col md={8}>
            <Card>
              <Card.Body>
                <NotificationForm
                  formData={formData}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  isPending={isPending}
                  disabled={selectedUserIds.length < 1}
                />
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Modal for message input (Mobile only) */}
      <Modal
        show={showMessageModal}
        onHide={() => setShowMessageModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Send Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NotificationForm
            formData={formData}
            onChange={handleChange}
            onSubmit={handleSubmit}
            isPending={isPending}
            disabled={selectedUserIds.length < 1}
          />
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Notification Sent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Your notification has been successfully sent to the selected user(s).
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmationModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NotificationPanel;
